import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { GoogleSignIn } from "shared/components/Auth/GoogleSignIn";
import InputGlobal from "shared/components/Form/InputGlobal";
import SpinLoader, {
  LOADER_COLOR_DARK,
  LOADER_SIZE_LARGE,
  POSITION_ABSOLUTE,
} from "shared/components/SpinLoader";

import { ERROR_STATUS_UNPROCESSABLE } from "shared/constants";
import {
  getErrorEventEmail,
  getErrorEventPassword,
} from "shared/constants/auth";
import { VALENTINES_MODAL_LOGIN_SESSION_KEY } from "shared/constants/valentinesDay";
import { WEB_PUSH_TYPE } from "shared/constants/webPush";

import { simplyClick } from "shared/helpers/analytics.js";
import {
  afterLoginSuccess,
  clearForm,
  prepareLoginData,
} from "shared/helpers/auth";
import { validateEmail, validatePassword } from "shared/helpers/validation";

import { useCurrentCountry } from "shared/hooks/useCurrentCountry";

import { loginUser } from "shared/services/auth";

import { ReactComponent as EyeIcon } from "assets/shared/img/icons/eye.svg";
import { ReactComponent as EyeIconHide } from "assets/shared/img/icons/eye-crossed.svg";

const LoginForm = () => {
  const { t } = useTranslation("auth");
  const [errorEmail, setErrorEmail] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [loginData, setLoginData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setLoader] = useState(false);
  const [responseError, setResponseError] = useState(false);
  const isLoginPage = window.location.pathname.includes("/login/");

  useCurrentCountry();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encodedEmail = params.get("email");

    if (encodedEmail) {
      const decodedEmail = decodeURIComponent(encodedEmail);
      setLoginData((prev) => ({
        ...prev,
        email: decodedEmail,
      }));
    }
  }, []);

  useEffect(() => {
    clearForm(".close__icon", ".background__modal", () => clearLoginForm());

    if (window.location.search !== "") {
      const signInButton = document.querySelector(".sign__up-button");
      const logoButton = document.querySelector(".logo_wrap");
      signInButton?.setAttribute("href", `/${window.location.search}`);
      logoButton?.setAttribute("href", `/${window.location.search}`);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        const loginModal = document.querySelector(".login__modal");
        loginModal.classList.remove("show");
        document.body.style.overflow = "unset";
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /*
   * Open Restore Modal and clean login user data
   * */
  const openRestoreModal = () => {
    const loginModal = document.querySelector(".login__modal");
    const restorePassModal = document.querySelector(".restore__modal");

    loginModal.classList.remove("show");
    restorePassModal.classList.add("show");
    document.body.style.overflow = "hidden";
    clearLoginForm();

    simplyClick(
      isLoginPage ? "cl_lnd_sign_in_login_forgot" : "cl_home_p_login_forgot"
    );

    if (restorePassModal.classList.contains("show")) {
      simplyClick(
        isLoginPage ? "sh_lnd_sign_in_reset_pass" : "sh_home_p_reset_pass"
      );
    }
  };

  /*
   * Clean Form
   * */
  const clearLoginForm = () => {
    setErrorEmail(null);
    setPasswordError(null);
    setResponseError(null);
    setLoginData({});
  };

  /**
   * Handler for change Email
   * @param {object} event
   */
  const handleChangeEmail = ({ target: { value } }) => {
    setErrorEmail(null);
    setLoginData((prev) => ({
      ...prev,
      email: value,
    }));
  };

  /**
   * Handler for change Password
   * @param {object} event
   */
  const handleSetPassword = (event) => {
    setPasswordError(null);
    setLoginData({ ...loginData, password: event?.target?.value });
  };

  /*
   * Toggle show Password on click icon eye
   * */
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Handler for submit login form and send request on api
   */
  const handleSubmitLogin = (e) => {
    e.preventDefault();

    const resultEmail = validateEmail(loginData.email);

    if (!resultEmail.status && resultEmail.error) {
      setErrorEmail(resultEmail.error);
      simplyClick(
        getErrorEventEmail(
          resultEmail.error,
          isLoginPage ? "sign_in" : "p_login",
          isLoginPage ? "lnd" : "home"
        )
      );
    }

    const resultPassword = validatePassword(loginData.password, null, true);

    if (!resultPassword.status) {
      setPasswordError(resultPassword.errorPassword.text);
      simplyClick(
        getErrorEventPassword(
          resultPassword.errorPassword.type,
          isLoginPage ? "sign_in" : "p_login",
          isLoginPage ? "lnd" : "home"
        )
      );
      return;
    }
    simplyClick(
      isLoginPage ? "cl_lnd_sign_in_login_sign_in" : "cl_home_p_login_sign_in"
    );

    if (resultEmail.status && resultPassword.status) {
      setLoader(true);
      loginUser(prepareLoginData(loginData))
        .then((resp) => cbLoginSuccess(resp))
        .catch((err) => responseErrorCb(err));
    }
  };

  /**
   * Success api response
   * @param {object} response APi response object
   */
  const cbLoginSuccess = (response) => {
    sessionStorage.setItem(WEB_PUSH_TYPE.INACTIVE, "true");
    sessionStorage.setItem(VALENTINES_MODAL_LOGIN_SESSION_KEY, "true");

    afterLoginSuccess(
      response,
      () => setLoginData({}),
      () => setLoader(false)
    );
  };

  /**
   * Failed request on api
   * @param {object} responseErr failed request on api
   */
  const responseErrorCb = (responseErr) => {
    setLoader(false);
    if (responseErr.response.status === ERROR_STATUS_UNPROCESSABLE) {
      const { data } = responseErr.response;
      let errorText = "";
      data.errors.forEach((error) => {
        if (error.name === "password") {
          setPasswordError(error.message);
          simplyClick(
            isLoginPage
              ? "err_lnd_sign_in_validation_notvalid"
              : "err_home_p_login_validation_notvalid"
          );
        } else if (error.name === "email") {
          setErrorEmail(error.message);
          simplyClick(
            isLoginPage
              ? "err_lnd_sign_in_validation_notvalid"
              : "err_home_p_login_validation_notvalid"
          );
        } else {
          errorText +=
            errorText.length > 0 ? " " + error.message : error.message;
        }
      });
    } else {
      setResponseError(t("loginRespError"));
    }
  };

  return (
    <>
      {loader && (
        <SpinLoader
          color={LOADER_COLOR_DARK}
          size={LOADER_SIZE_LARGE}
          position={POSITION_ABSOLUTE}
          withBlur={true}
        />
      )}

      <form onSubmit={handleSubmitLogin} noValidate>
        <div className="login__body">
          <label htmlFor="email" className="label_global">
            {t("labelEmail")}
          </label>
          <InputGlobal
            className={"register__email"}
            handleChange={(e) => handleChangeEmail(e)}
            name="email"
            id="email"
            value={loginData.email || ""}
            error={!!errorEmail}
            errorText={errorEmail}
            placeholder={t("placeholderEmail")}
            autoComplete="email"
            aria-invalid={!!errorEmail}
            aria-describedby={errorEmail ? "email-error" : undefined}
            onFocus={() =>
              simplyClick(
                isLoginPage
                  ? "cl_lnd_sign_in_login_email_type"
                  : "cl_home_p_login_email_type"
              )
            }
          />
          <label htmlFor="password" className="label_global">
            {t("passwordLabel")}
          </label>
          <InputGlobal
            id="password"
            className={"register__password"}
            handleChange={(val) => handleSetPassword(val)}
            name="password"
            type={showPassword ? "text" : "password"}
            value={loginData.password}
            error={!!passwordError}
            errorText={passwordError}
            placeholder={t("passwordPlaceholder")}
            rightIconSrc={showPassword ? <EyeIcon /> : <EyeIconHide />}
            handleClickRightIcon={toggleShowPassword}
            passwordShowIcon={showPassword}
            autoComplete="current-password"
            aria-invalid={!!passwordError}
            aria-describedby={passwordError ? "password-error" : undefined}
            onFocus={() =>
              simplyClick(
                isLoginPage
                  ? "cl_lnd_sign_in_login_pass_type"
                  : "cl_home_p_login_pass_type"
              )
            }
          />
        </div>
        <div className="login__footer">
          <button
            type="submit"
            className="form__button"
            aria-label={t("signInButton")}
            onClick={handleSubmitLogin}
          >
            {t("signInButton")}
          </button>
          <button
            type="button"
            onClick={openRestoreModal}
            className="forgot__pass"
          >
            {t("forgotPassButton")}
          </button>
          {responseError && <p className="error response">{responseError}</p>}
          {import.meta.env.VITE_APP_GOOGLE_AUTH_CLIENT_ID && (
            <>
              <span className="line"></span>
              <GoogleSignIn
                setLoader={setLoader}
                setResponseError={setResponseError}
              />
            </>
          )}
        </div>
      </form>
    </>
  );
};

export default LoginForm;
